/**
 * Server-Sent Events (SSE) Manager for R.U.S.H. Game
 * 
 * Provides real-time game state updates via SSE (Server-Sent Events).
 * SSE is simpler and more cost-effective than WebSockets, with built-in reconnection.
 * 
 * Features:
 * - Auto-reconnect with exponential backoff
 * - Works in LocalStack and AWS DEV environments
 * - Graceful fallback to polling when SSE is unavailable
 * - Type-safe message handling
 */

import { config } from '../config/env';
import { networkLogger } from './networkLogger';

export interface SSEMessage {
  type: string;
  gameId?: string;
  gameState?: any;
  phase?: string;
  oldPhase?: string;
  newPhase?: string;
  timestamp?: string;
  error?: string;
}

type SSEMessageHandler = (message: SSEMessage) => void;
type SSEErrorHandler = (error: Event) => void;
type SSEConnectionHandler = () => void;

export class SSEManager {
  private eventSource: EventSource | null = null;
  private messageHandlers: SSEMessageHandler[] = [];
  private errorHandlers: SSEErrorHandler[] = [];
  private connectionHandlers: SSEConnectionHandler[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private gameId: string | null = null;
  private userId: string | null = null;
  private isManuallyDisconnected = false;

  constructor() {
    console.log('📡 SSE Manager initialized');
  }

  /**
   * Connect to SSE endpoint for a specific game.
   * The connection will auto-reconnect on errors with exponential backoff.
   */
  connect(gameId: string, userId: string): Promise<void> {
    // Check if SSE is configured (VITE_SSE_URL must be explicitly set)
    // In LocalStack mode, SSE is not available - use polling fallback instead
    if (!config.realtime.sseUrl || config.realtime.sseUrl.trim() === '') {
      console.log('📭 SSE not configured - using polling fallback (LocalStack mode)');
      return Promise.reject(new Error('SSE not configured'));
    }
    
    // Check if SSE endpoint is configured
    if (!config.api.baseUrl) {
      console.warn('📭 SSE disabled: API base URL not configured');
      return Promise.reject(new Error('API base URL not configured'));
    }

    // If already connected to the same game, don't reconnect
    if (
      this.eventSource &&
      this.eventSource.readyState === EventSource.OPEN &&
      this.gameId === gameId &&
      this.userId === userId
    ) {
      console.log('✅ SSE already connected to', gameId);
      return Promise.resolve();
    }

    // Disconnect existing connection if connecting to different game
    if (this.eventSource && (this.gameId !== gameId || this.userId !== userId)) {
      console.log('🔄 Switching SSE connection to new game');
      this.disconnect();
    }

    this.gameId = gameId;
    this.userId = userId;
    this.isManuallyDisconnected = false;

    // Construct SSE URL
    const sseUrl = `${config.api.baseUrl}/games/${gameId}/events?userId=${userId}`;
    console.log('📡 Connecting to SSE:', sseUrl);

    return new Promise((resolve, reject) => {
      try {
        this.eventSource = new EventSource(sseUrl);

        this.eventSource.onopen = () => {
          console.log('✅ SSE connected to game:', gameId);
          this.reconnectAttempts = 0;
          this.connectionHandlers.forEach(handler => handler());
          resolve();
        };

        // Handle custom event types from the server
        this.eventSource.addEventListener('game_state_update', (event: MessageEvent) => {
          try {
            const message: SSEMessage = JSON.parse(event.data);
            console.log('📨 SSE game_state_update:', message.type);
            
            // Log SSE event
            networkLogger.logSSE({
              eventType: 'game_state_update',
              data: message,
            });
            
            this.messageHandlers.forEach(handler => handler(message));
          } catch (error) {
            console.error('❌ Failed to parse SSE message:', error);
          }
        });

        this.eventSource.addEventListener('error', (event: MessageEvent) => {
          try {
            const message: SSEMessage = JSON.parse(event.data);
            console.error('❌ SSE error event:', message);
          } catch {
            console.error('❌ SSE error event (unparseable)');
          }
        });

        // Handle default 'message' events (fallback)
        this.eventSource.onmessage = (event: MessageEvent) => {
          try {
            const message: SSEMessage = JSON.parse(event.data);
            console.log('📨 SSE message:', message.type || 'unknown');
            
            // Log SSE event
            networkLogger.logSSE({
              eventType: message.type || 'message',
              data: message,
            });
            
            this.messageHandlers.forEach(handler => handler(message));
          } catch (error) {
            console.error('❌ Failed to parse SSE message:', error);
          }
        };

        this.eventSource.onerror = (error: Event) => {
          console.error('❌ SSE connection error');
          this.errorHandlers.forEach(handler => handler(error));

          // Auto-reconnect if not manually disconnected
          if (!this.isManuallyDisconnected) {
            this.scheduleReconnect();
          }

          // Reject promise only on first connection failure
          if (this.reconnectAttempts === 0) {
            reject(error);
          }
        };
      } catch (error) {
        console.error('❌ Failed to create SSE connection:', error);
        reject(error);
      }
    });
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.isManuallyDisconnected || !this.gameId || !this.userId) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max SSE reconnection attempts reached, falling back to polling');
      return;
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, ... up to 30s max
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;

    console.log(`🔄 Reconnecting SSE in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      if (this.gameId && this.userId) {
        this.connect(this.gameId, this.userId).catch(error => {
          console.error('❌ SSE reconnect failed:', error);
        });
      }
    }, delay);
  }

  /**
   * Subscribe to SSE messages
   * Returns unsubscribe function
   */
  onMessage(handler: SSEMessageHandler): () => void {
    this.messageHandlers.push(handler);
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Subscribe to SSE errors
   * Returns unsubscribe function
   */
  onError(handler: SSEErrorHandler): () => void {
    this.errorHandlers.push(handler);
    return () => {
      this.errorHandlers = this.errorHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Subscribe to connection events
   * Returns unsubscribe function
   */
  onConnection(handler: SSEConnectionHandler): () => void {
    this.connectionHandlers.push(handler);
    return () => {
      this.connectionHandlers = this.connectionHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Disconnect from SSE endpoint
   */
  disconnect(): void {
    console.log('🔌 Disconnecting SSE');
    this.isManuallyDisconnected = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.gameId = null;
    this.userId = null;
    this.reconnectAttempts = 0;
  }

  /**
   * Check if SSE is currently connected
   */
  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }

  /**
   * Get current connection state
   */
  getState(): {
    connected: boolean;
    gameId: string | null;
    userId: string | null;
    reconnectAttempts: number;
  } {
    return {
      connected: this.isConnected(),
      gameId: this.gameId,
      userId: this.userId,
      reconnectAttempts: this.reconnectAttempts,
    };
  }
}

// Export singleton instance
export const sseManager = new SSEManager();


